import React, {useEffect, useRef, useState} from 'react';

import Clipboard from 'clipboard';
import {Tooltip} from 'reactstrap';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useSetTimeout} from 'app/utils/hooks';
import {gt} from 'app/utils/i18n';
import {getLocation} from 'app/utils/urls';

interface Props {
  disabled?: boolean;
  tooltip: string;
  text: string;
  className?: string;
  children: React.ReactNode;
}

export function CopyTextButton({
  disabled = false,
  tooltip,
  text,
  className = '',
  children,
}: Props): JSX.Element {
  const [showCopied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const clipboardRef = useRef<null | Record<string, any>>(null);
  const setCopiedTimeout = useSetTimeout();
  let clipboard = clipboardRef.current;

  /* instantiate clipboard.js */
  useEffect(() => {
    const buttonEl = buttonRef.current;
    if (buttonEl != null) {
      clipboard = clipboardRef.current = new Clipboard(buttonEl);
      return () => {
        clipboard != null && clipboard.destroy();
      };
    }
  }, [buttonRef]);

  /* show tooltip on copy success */
  useEffect(() => {
    if (clipboard != null) {
      const successHandler = () => {
        setCopied(true);
        setCopiedTimeout(() => setCopied(false), 750);
      };

      clipboard.on('success', successHandler);
      return () => {
        clipboard != null && clipboard.off('success', successHandler);
      };
    }
  }, [clipboard, setCopiedTimeout, setCopied]);
  return (
    <>
      {buttonRef.current != null ? (
        <Tooltip isOpen={showCopied} target={buttonRef.current}>
          {gt('Copied!')}
        </Tooltip>
      ) : null}
      <LazyTooltip hide={showCopied || disabled} tooltip={tooltip}>
        <button
          ref={buttonRef}
          className={className}
          disabled={disabled}
          data-clipboard-text={text}
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {children}
        </button>
      </LazyTooltip>
    </>
  );
}

interface CopyLinkButtonProps {
  disabled?: boolean;
  tooltip: string;
  link: string;
  className?: string;
  children?: React.ReactNode;
}
export function CopyLinkButton({
  disabled = false,
  tooltip,
  link,
  className = '',
  children,
}: CopyLinkButtonProps): JSX.Element {
  return (
    <CopyTextButton
      className={className}
      text={getLocation(link).href}
      tooltip={tooltip}
      disabled={disabled}
    >
      {children != null ? children : <i className="fa fa-link" />}
    </CopyTextButton>
  );
}
